import {
  useRef,
  useEffect,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import TuiImageEditor from "tui-image-editor";
import "tui-image-editor/dist/tui-image-editor.css";
import Dropzone from "react-dropzone";
import {
  Popper,
  Button,
  Dialog,
  Slide,
  IconButton,
  Snackbar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ReactComponent as UndoIcon } from "../../../assets/img/svg/editor/undo.svg";
import { ReactComponent as RedoIcon } from "../../../assets/img/svg/editor/redo.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/img/svg/editor/delete.svg";
import { ReactComponent as HandIcon } from "../../../assets/img/svg/editor/hand.svg";
import { ReactComponent as ZoomInIcon } from "../../../assets/img/svg/editor/zoomInIcon.svg";
import { ReactComponent as ZoomOutIcon } from "../../../assets/img/svg/editor/zoomOutIcon.svg";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import CustomButton from "../../../ui/button/Button";
import Crop32Icon from "@mui/icons-material/Crop32";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import BrushIcon from "@mui/icons-material/Brush";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import SelectAutoComplete from "../../../ui/selectAutoComplete/SelectAutoComplete";
import { dataURLtoFile, compressImageFile } from "../../../utils/miscHelpers";
import "./Editor.css";
import SelectorDeCategoriaModal from "./SelectorDeCategoriaModal";

const toolsButtonSx = {
  color: "#223D51",
  textTransform: "none",
  fontWeight: "bold",
};

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ImageEditor = forwardRef(function ImageEditor(
  {
    cambiarImagen,
    onUpload,
    imagenesCategorias,
    handleNextImage,
    subirMultiplesImagenes,
    ...props
  },
  ref
) {
  const rootEl = useRef(null);
  const [editor, setEditor] = useState(null);

  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [scale, setScale] = useState(1);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [drawMode, setDrawMode] = useState("none");
  const [type, setType] = useState("image/jpeg");
  const [selectedCategoria, setSelectedCategoria] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [color, setColor] = useState("#223d51");
  const [flipDims, setFlipDims] = useState(true);

  const roRef = useRef(null);

  const [isHandMode, setIsHandMode] = useState(false);
  const handHandlersRef = useRef({ down: null, move: null, up: null });

  const fileInputRef = useRef(null);

  const [openSelector, setOpenSelector] = useState(false);
  const [multipleFilesQueue, setMultipleFilesQueue] = useState([]);

  useEffect(() => {
    return () => roRef.current?.disconnect?.();
  }, []);

  const getFabricCanvas = useCallback(() => {
    return editor?._graphics?.getCanvas?.() || editor?.getCanvas?.() || null;
  }, [editor]);

  const setObjectsInteractable = useCallback((canvas, interactable) => {
    if (!canvas) return;
    canvas.selection = interactable;
    canvas.forEachObject((obj) => {
      obj.selectable = interactable;
      obj.evented = interactable;
    });
    canvas.defaultCursor = interactable ? "default" : "grab";
    canvas.hoverCursor = interactable ? "move" : "grab";
    canvas.requestRenderAll();
  }, []);

  const disableHandMode = useCallback(() => {
    const canvas = getFabricCanvas();
    if (!canvas) return;
    const { down, move, up } = handHandlersRef.current || {};
    if (down) canvas.off("mouse:down", down);
    if (move) canvas.off("mouse:move", move);
    if (up) canvas.off("mouse:up", up);
    handHandlersRef.current = { down: null, move: null, up: null };
    setObjectsInteractable(canvas, true);
    setIsHandMode(false);
  }, [getFabricCanvas, setObjectsInteractable]);

  useEffect(() => {
    return () => {
      disableHandMode();
    };
  }, [disableHandMode]);

  useEffect(() => {
    if (!isEditorOpen && editor) {
      try {
        editor.destroy();
      } catch {}
      setEditor(null);
    }
    return () => {
      if (editor) {
        try {
          editor.destroy();
        } catch {}
      }
    };
  }, [isEditorOpen, editor]);

  const handleNewFile = (e) => {
    fileInputRef.current.click();
  };

  const openEditorWithFile = useCallback(
    (file, isEdit = false, originalImageData = null) => {
      if (!file) return;
      setIsEditorOpen(true);
      setIsEditing(isEdit);
      setPendingFile(file);
      if (originalImageData && imagenesCategorias?.length) {
        const cat =
          imagenesCategorias.find(
            (c) => c.nombre === originalImageData.comentarios
          ) || null;
        setSelectedCategoria(cat);
      } else {
        setSelectedCategoria(null);
      }
    },
    [imagenesCategorias]
  );

  const handleNextImageEditor = async (file, img) => {
    setPendingFile(file);
    if (imagenesCategorias?.length) {
      const cat =
        imagenesCategorias.find((c) => c.nombre === img.comentarios) || null;
      setSelectedCategoria(cat);
    } else {
      setSelectedCategoria(null);
    }

    if (!rootEl.current) return;

    if (editor?.destroy) {
      try {
        editor.destroy();
      } catch {}
      setEditor(null);
    }

    const instance = new TuiImageEditor(rootEl.current, {
      cssMaxWidth: 2000,
      cssMaxHeight: 1200,
      selectionStyle: { cornerSize: 40, rotatingPointOffset: 70 },
      usageStatistics: false,
      ...props,
    });

    setEditor(instance);

    setType(file.type);
    try {
      const tiny = await compressImageFile(file, {
        maxWidth: 2000,
        maxHeight: 1200,
        type: file.type || "image/jpeg",
        quality: 0.9,
      });
      await instance.loadImageFromFile(tiny);

      const size = instance.getCanvasSize();

      setInitialCanvasState(size);

      instance.clearUndoStack();
    } catch (e) {
      console.error("Failed to load image:", e);
    }
  };

  async function copyImageToClipboard(file = pendingFile) {
    const img = new Image();
    img.src = URL.createObjectURL(file);

    await img.decode();

    const canvas = document.createElement("canvas");
    canvas.width = img.width;
    canvas.height = img.height;

    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0);

    canvas.toBlob(async (blob) => {
      try {
        await navigator.clipboard.write([
          new ClipboardItem({ "image/png": blob }),
        ]);
      } catch (err) {
        console.error(err);
      }
    }, "image/png");
  }

  useImperativeHandle(ref, () => ({
    openWithFile: openEditorWithFile,
    handleNext: handleNextImageEditor,
  }));

  const enableHandMode = useCallback(() => {
    stopDraw();
    const canvas = getFabricCanvas();
    if (!canvas) return;

    const down = (opt) => {
      canvas.isDragging = true;
      canvas.lastPosX = opt.e.clientX;
      canvas.lastPosY = opt.e.clientY;
      canvas.setCursor("grabbing");
      canvas.requestRenderAll();
    };
    const move = (opt) => {
      if (!canvas.isDragging) return;
      const e = opt.e;
      const vpt = canvas.viewportTransform;
      vpt[4] += e.clientX - canvas.lastPosX;
      vpt[5] += e.clientY - canvas.lastPosY;
      canvas.lastPosX = e.clientX;
      canvas.lastPosY = e.clientY;
      canvas.requestRenderAll();
    };
    const up = () => {
      canvas.isDragging = false;
      canvas.setCursor("grab");
      canvas.requestRenderAll();
    };

    handHandlersRef.current = { down, move, up };
    canvas.on("mouse:down", down);
    canvas.on("mouse:move", move);
    canvas.on("mouse:up", up);

    setObjectsInteractable(canvas, false);
    setIsHandMode(true);
  }, [getFabricCanvas, setObjectsInteractable]);

  const toggleHandMode = useCallback(() => {
    if (isHandMode) disableHandMode();
    else enableHandMode();
  }, [isHandMode, enableHandMode, disableHandMode]);

  const handleFileChange = useCallback(async (file) => {
    if (file) {
      setPendingFile(file);
      setIsEditorOpen(true);
    }
  }, []);

  const handleDialogEntered = useCallback(async () => {
    if (!rootEl.current) return;

    if (editor?.destroy) {
      try {
        editor.destroy();
      } catch {}
      setEditor(null);
    }

    const instance = new TuiImageEditor(rootEl.current, {
      cssMaxWidth: 2000,
      cssMaxHeight: 1200,
      selectionStyle: { cornerSize: 40, rotatingPointOffset: 70 },
      usageStatistics: false,
      ...props,
    });

    setEditor(instance);

    if (pendingFile) {
      setType(pendingFile.type);
      try {
        const tiny = await compressImageFile(pendingFile, {
          maxWidth: 2000,
          maxHeight: 1200,
          type: pendingFile.type || "image/jpeg",
          quality: 0.9,
        });
        await instance.loadImageFromFile(tiny);

        const size = instance.getCanvasSize();

        setInitialCanvasState(size);

        instance.clearUndoStack();
      } catch (e) {
        console.error("Failed to load image:", e);
      }
    }
  }, [editor, pendingFile, props]);

  const setInitialCanvasState = (size) => {
    let w = size.width;
    let h = size.height;
    const maxDim = Math.max(w, h);

    if (w > 1200 || h > 700) {
      w = w / 2;
      h = h / 2;
    }

    rootEl.current.style.width = `${w}px`;
    rootEl.current.style.height = `${h}px`;

    setScale(maxDim / 5);
  };

  const loadNewImage = async (file) => {
    if (!file || !editor) return;
    try {
      const tiny = await compressImageFile(file, {
        maxWidth: 2000,
        maxHeight: 1200,
        type: file.type || "image/jpeg",
        quality: 0.9,
      });
      await editor.loadImageFromFile(tiny);

      const size = editor.getCanvasSize();

      setInitialCanvasState(size);

      editor.clearUndoStack();
    } catch (e) {
      console.error("Failed to load image:", e);
    }
  };

  const handleSave = async () => {
    if (!editor) return;

    if (!selectedCategoria && imagenesCategorias?.length > 0) {
      setSnackbarMessage("Debes seleccionar una categoría de imagen.");
      setShowSnackbar(true);
      return;
    }

    try {
      const format = type === "image/jpeg" ? "jpeg" : "png";
      const dataURL = editor.toDataURL({
        format,
      });

      const fileName = `image.${format}`;
      const file = dataURLtoFile(dataURL, fileName);

      if (typeof onUpload === "function") {
        if (isEditing) {
          await cambiarImagen(file, true, selectedCategoria);
        } else {
          await onUpload(file, selectedCategoria);
        }
      } else {
        console.warn("No onUpload prop provided; edited file ready:", file);
      }
      handleClose();
    } catch (e) {
      console.error("Failed to save/upload edited image:", e);
    }
  };

  const handleResize = () => {
    let { width, height } = editor.getCanvasSize();
    if (width > 1200 || height > 700) {
      width = width / 2;
      height = height / 2;
    }
    if (flipDims) {
      rootEl.current.style.width = `${height}px`;
      rootEl.current.style.height = `${width}px`;
      setFlipDims(false);
    } else {
      rootEl.current.style.width = `${width}px`;
      rootEl.current.style.height = `${height}px`;
      setFlipDims(true);
    }
  };

  const handleClose = useCallback(() => {
    setIsEditorOpen(false);
    setPendingFile(null);
    setDrawMode("none");
    setSelectedCategoria(null);
    setAnchorEl(null);
    setColor("#223d51");
    disableHandMode();
    setEditor(null);
    setFlipDims(true);
    if (editor) {
      editor._clearHistory();
    }
    setIsEditing(false);
  }, []);

  const undo = async () => {
    try {
      const undoRes = await editor?.undo();
      if (undoRes !== undefined && typeof undoRes === "number") {
        handleResize();
      }
    } catch (e) {
      console.log(e);
    }
  };

  const redo = async () => {
    try {
      const redoRes = await editor?.redo();
      if (redoRes !== undefined && typeof redoRes === "number") {
        handleResize();
      }
    } catch (e) {
      console.log(e);
    }
  };

  const startDraw = () => {
    if (drawMode === "draw") {
      stopDraw();
    } else {
      if (isHandMode) disableHandMode();
      setDrawMode("draw");
      editor?.startDrawingMode("FREE_DRAWING", {
        width: Math.round(scale / 30),
        color: color,
      });
    }
  };

  const stopDraw = () => {
    setDrawMode("none");
    editor?.stopDrawingMode();
  };

  const addText = () => {
    if (isHandMode) disableHandMode();
    const defaultFontSize = Math.round(scale / 6);
    editor?.stopDrawingMode();
    setDrawMode("text");
    editor?.addText("Texto", {
      styles: {
        fill: color,
        fontSize: defaultFontSize,
        fontFamily: "Arial",
      },
    });
  };

  const addRect = () => {
    if (isHandMode) disableHandMode();
    editor?.addShape("rect", {
      fill: "transparent",
      stroke: color,
      strokeWidth: scale / 25,
      width: scale,
      height: scale / 2,
    });
  };

  const addCircle = () => {
    if (isHandMode) disableHandMode();
    editor?.addShape("circle", {
      fill: "transparent",
      stroke: color,
      strokeWidth: scale / 25,
      rx: scale / 2,
      ry: scale / 2,
    });
  };

  const rotateLeft = () => {
    handleResize();
    editor?.rotate(-90);
  };

  const flipX = () => editor?.flipX();

  const flipY = () => editor?.flipY();

  const deleteObj = async () => {
    editor.startDrawingMode("NORMAL");
    try {
      editor?.removeActiveObject();
    } catch (error) {
      console.log(error);
    }
  };

  const enterCrop = (e) => {
    if (isHandMode) disableHandMode();
    setAnchorEl(e.currentTarget);
    setDrawMode("crop");
    editor?.startDrawingMode("CROPPER");
  };

  const applyCrop = async () => {
    try {
      setAnchorEl(null);
      await editor?.crop(editor.getCropzoneRect());
      editor?.stopDrawingMode();
      setDrawMode("none");
    } catch (e) {
      console.warn("No crop zone to apply:", e);
    }
  };
  const cancelCrop = () => {
    setAnchorEl(null);
    setDrawMode("none");
    editor?.stopDrawingMode();
  };

  const zoomIn = () => {
    let newLevel = zoomLevel + 0.2;
    if (newLevel > 5) newLevel = 5;
    editor.zoom({ x: 0, y: 0, zoomLevel: newLevel });
    setZoomLevel(newLevel);
  };

  const zoomOut = () => {
    let newLevel = zoomLevel - 0.2;
    if (newLevel < 1) newLevel = 1;
    editor.zoom({ x: 0, y: 0, zoomLevel: newLevel });
    setZoomLevel(newLevel);
  };

  const save = () => {
    if (!editor) return;
    const dataUrl = editor.toDataURL({
      format: type === "image/jpeg" ? "jpeg" : "png",
    });
    if (!dataUrl) return;
    const extFromDataURL = type === "image/jpeg" ? "jpeg" : "png";
    fetch(dataUrl)
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `image.${extFromDataURL}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      });
  };

  const handleUploadMultipleImages = async (categoria) => {
    await subirMultiplesImagenes(multipleFilesQueue, categoria);
    setMultipleFilesQueue([]);
  };

  return (
    <>
      <Dropzone
        accept={{ "image/*": [] }}
        multiple={true}
        onDrop={(acceptedFiles) => {
          if (!acceptedFiles || acceptedFiles.length === 0) return;
          if (acceptedFiles.length > 1) {
            setMultipleFilesQueue(acceptedFiles);
            setOpenSelector(true);
          } else {
            const file = acceptedFiles?.[0];
            if (file) handleFileChange(file);
          }
        }}
      >
        {({ getRootProps, getInputProps, isDragActive }) => (
          <section
            className="editor-drop-area"
            style={{
              backgroundColor: isDragActive ? "#eef7ff" : "#f9f9f9",
            }}
          >
            <div {...getRootProps()} style={{ padding: "40px" }}>
              <input {...getInputProps()} />
              <p>Imágenes</p>
              <p>{"(Seleccionar o arrastrar archivo)"}</p>
            </div>
          </section>
        )}
      </Dropzone>

      <Dialog
        open={isEditorOpen}
        onClose={handleClose}
        slotProps={{
          transition: {
            onEntered: handleDialogEntered,
          },
        }}
        slots={{ transition: Transition }}
        keepMounted
        fullWidth
        maxWidth="lg"
        disableEnforceFocus
      >
        <Snackbar
          open={showSnackbar}
          autoHideDuration={3000}
          onClose={() => {
            setShowSnackbar(false);
            setSnackbarMessage("");
          }}
          message={snackbarMessage}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          slotProps={{
            content: {
              sx: {
                backgroundColor: "white",
                color: "#222",
                justifyContent: "center",
                textAlign: "center",
                fontWeight: "bold",
                fontSize: "16px",
                fontFamily: "Roboto, Arial, sans-serif",
              },
            },
          }}
        />
        <div className="editor-container">
          <div className="editor-header">
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={(theme) => ({
                position: "absolute",
                right: 2,
                top: 6,
                color: "gray",
              })}
            >
              <CloseIcon />
            </IconButton>
            <h2 style={{ margin: 0 }}>Editor de imagen</h2>
            <div className="editor-header-toolbar">
              <div></div>
              <div style={{ display: "flex", gap: 8 }}>
                <IconButton
                  aria-label="hand"
                  onClick={toggleHandMode}
                  sx={{
                    backgroundColor: isHandMode ? "lightblue" : "white",
                    "&:hover": { backgroundColor: "lightblue" },
                  }}
                  title={isHandMode ? "Salir de mano" : "Herramienta mano"}
                >
                  <HandIcon />
                </IconButton>
                <IconButton aria-label="zoomIn" onClick={zoomIn}>
                  <ZoomInIcon />
                </IconButton>
                <IconButton aria-label="zoomOut" onClick={zoomOut}>
                  <ZoomOutIcon />
                </IconButton>
                <IconButton aria-label="undo" onClick={undo}>
                  <UndoIcon />
                </IconButton>
                <IconButton aria-label="redo" onClick={redo}>
                  <RedoIcon />
                </IconButton>
                <IconButton aria-label="delete" onClick={deleteObj}>
                  <DeleteIcon />
                </IconButton>
              </div>

              <div style={{ display: "flex", gap: 8 }}>
                <CustomButton
                  background={"#223D51"}
                  color={"#FFFFFF"}
                  text="Cargar"
                  height="40px"
                  fontWeight="700"
                  fontSize="14px"
                  border="1px solid #D9D9D9"
                  type="button"
                  width={"100px"}
                  onClick={handleNewFile}
                />
                <CustomButton
                  background={"#223D51"}
                  color={"#FFFFFF"}
                  text="Descargar"
                  height="40px"
                  fontWeight="700"
                  fontSize="14px"
                  border="1px solid #D9D9D9"
                  onClick={save}
                  type="button"
                  width={"100px"}
                />
              </div>
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={(e) => loadNewImage(e.target.files[0])}
              />
            </div>
          </div>

          <div className="editor-body">
            <div className="editor-body-top">
              <div className="editor-body-sidebar">
                <IconButton aria-label="addText" sx={{ visibility: "hidden" }}>
                  <TextFieldsIcon fontSize="large" />
                </IconButton>
                <label className="editor-color-picker-label">
                  <input
                    type="color"
                    value={color}
                    onChange={(e) => {
                      if (drawMode === "draw") {
                        editor.setBrush({
                          color: e.target.value.slice(1),
                        });
                      }
                      setColor(e.target.value);
                    }}
                    className="editor-color-picker-input"
                    aria-label="Color picker"
                  />
                </label>
                <IconButton
                  aria-label="startDraw"
                  onClick={startDraw}
                  sx={{
                    backgroundColor:
                      drawMode === "draw" ? "lightblue" : "white",
                    "&:hover": {
                      backgroundColor: "lightblue",
                    },
                  }}
                >
                  <BrushIcon
                    color={drawMode === "draw" ? "primary" : "inherit"}
                    fontSize="large"
                  />
                </IconButton>
                <IconButton aria-label="addText" onClick={addText}>
                  <TextFieldsIcon fontSize="large" />
                </IconButton>
                <IconButton aria-label="addRect" onClick={addRect}>
                  <Crop32Icon fontSize="large" />
                </IconButton>
                <IconButton aria-label="addCircle" onClick={addCircle}>
                  <RadioButtonUncheckedIcon fontSize="large" />
                </IconButton>
              </div>
              <div className="editor-body-main">
                <div className="editor-body-main-tools">
                  <Popper
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    placement="right-start"
                    style={{ zIndex: 999999999 }}
                  >
                    <div
                      style={{
                        display: "flex",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                      }}
                    >
                      <IconButton sx={{ color: "#223D51" }} onClick={applyCrop}>
                        <CheckIcon />
                      </IconButton>
                      <IconButton
                        sx={{ color: "#223D51" }}
                        onClick={cancelCrop}
                      >
                        <ClearIcon />
                      </IconButton>
                    </div>
                  </Popper>
                  <Button
                    sx={toolsButtonSx}
                    size="small"
                    variant="text"
                    onClick={rotateLeft}
                  >
                    ⟲ Rotar
                  </Button>
                  <Button
                    sx={toolsButtonSx}
                    size="small"
                    variant="text"
                    onClick={flipX}
                  >
                    Espejo X
                  </Button>

                  <Button
                    sx={toolsButtonSx}
                    size="small"
                    variant="text"
                    onClick={flipY}
                  >
                    Espejo Y
                  </Button>
                  <Button
                    sx={toolsButtonSx}
                    size="small"
                    variant="text"
                    onClick={enterCrop}
                  >
                    Recortar
                  </Button>
                </div>
                <div className="editor-root-el" ref={rootEl} />
              </div>
            </div>
            <div className="editor-footer-actions">
              <div className="editor-footer-actions-prev-next">
                {isEditing && (
                  <CustomButton
                    background={"#FFFFFF"}
                    color={"#223D51"}
                    text="Anterior"
                    height="40px"
                    fontWeight="700"
                    fontSize="14px"
                    border="1px solid #D9D9D9"
                    onClick={() => handleNextImage(true)}
                    type="button"
                    width={"100px"}
                  />
                )}
                {isEditing && (
                  <CustomButton
                    background={"#FFFFFF"}
                    color={"#223D51"}
                    text="Siguiente"
                    height="40px"
                    fontWeight="700"
                    fontSize="14px"
                    border="1px solid #D9D9D9"
                    onClick={() => handleNextImage(false)}
                    type="button"
                    width={"100px"}
                  />
                )}
                <CustomButton
                  background={"#FFFFFF"}
                  color={"#223D51"}
                  text="Copiar imagen"
                  height="40px"
                  fontWeight="700"
                  fontSize="14px"
                  border="1px solid #D9D9D9"
                  onClick={() => copyImageToClipboard()}
                  type="button"
                  width={"130px"}
                />
              </div>
              <div className="editor-footer-actions-actions">
                <SelectAutoComplete
                  field="id"
                  fieldC="nombre"
                  options={imagenesCategorias || []}
                  value={selectedCategoria || null}
                  onChange={(e, value) => setSelectedCategoria(value)}
                  placeholder="--Selecciona comentario--"
                  size="small"
                />
                <CustomButton
                  background={"#FFFFFF"}
                  color={"#223D51"}
                  text="Cancelar"
                  height="40px"
                  fontWeight="700"
                  fontSize="14px"
                  border="1px solid #D9D9D9"
                  onClick={handleClose}
                  type="button"
                  width={"100px"}
                />
                <CustomButton
                  background={"#223D51"}
                  color={"#FFFFFF"}
                  text="Guardar"
                  height="40px"
                  fontWeight="700"
                  fontSize="14px"
                  border="1px solid #D9D9D9"
                  type="button"
                  width={"100px"}
                  onClick={handleSave}
                />
              </div>
            </div>
          </div>
        </div>
      </Dialog>

      <SelectorDeCategoriaModal
        open={openSelector}
        onClose={() => setOpenSelector(false)}
        onConfirm={(categoria) => {
          handleUploadMultipleImages(categoria);
          setOpenSelector(false);
        }}
        categorías={imagenesCategorias}
      />
    </>
  );
});

export default ImageEditor;
